import React from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";

/**
 * Stacked title + subtitle block used at the top of Section 3 panels
 * (Bio-Optimization Services, Workout plan, etc.).
 *
 *   ┌────────────────────────────────┐
 *   │ Title           (font-Medium)  │
 *   │ Subtitle copy   (font-Regular) │
 *   └────────────────────────────────┘
 *
 * Typography matches the Figma section header tokens; the two-pixel `gap`
 * matches the original inline `headerBlock` style that was duplicated across
 * several panels.
 */
type Props = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
};

const SectionHeader: React.FC<Props> = ({ title, subtitle }) => {
  const theme: Theme = useTheme();
  return (
    <Box sx={styles.headerBlock}>
      <Box component="h2" sx={styles.title(theme)}>
        {title}
      </Box>
      {subtitle && (
        <Box component="p" sx={styles.subtitle(theme)}>
          {subtitle}
        </Box>
      )}
    </Box>
  );
};

export default SectionHeader;

const styles = {
  headerBlock: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "2px",
  },
  title: (theme: Theme) => ({
    margin: 0,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: 1.4,
    letterSpacing: "-0.28px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  subtitle: (theme: Theme) => ({
    margin: 0,
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: 1.6,
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
};
